import i18n, {useTranslation} from '@/translations/i18n.tsx'
import {ModalBody, ModalFooter, ModalParagraph, ModalTitle} from '@components/ui/modal-atoms'
import {Dispatch, FC, SetStateAction} from 'react'
import {InputHelpText} from '@components/ui/input-help-text'
import {Spinner} from '@components/ui/spinner'
import {Button} from '@components/ui/button'
import {Controller, SubmitHandler, useForm} from 'react-hook-form'
import {z} from 'zod'
import {zodResolver} from '@hookform/resolvers/zod'
import {Label} from '@components/ui/label'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/bootstrap.css'
import {useRequestVerificationPhoneCode} from '@features/settings/queries/useRequestVerificationPhoneCode'

const SmsOtpFlowSchema = z.object({
    phone: z.string().min(10, {message: i18n.t('settings:personal_information:phone_error')})
})
export type PhoneValidationSchema = z.infer<typeof SmsOtpFlowSchema>

interface OtpSmsEditStepProps {
    phoneNumber: string | null
    onClose: () => void
    setPhoneNumberValue: Dispatch<SetStateAction<string>>
    setOtpStep: Dispatch<SetStateAction<'edit' | 'pending_confirmation'>>
}

export const OtpSmsEditStep: FC<OtpSmsEditStepProps> = ({phoneNumber, setOtpStep, setPhoneNumberValue, onClose}) => {
    const {t} = useTranslation()

    const requestVerificationPhoneCodeMutation = useRequestVerificationPhoneCode({
        onSuccess: () => {
            setOtpStep('pending_confirmation')
        }
    })

    const {
        control,
        handleSubmit,
        watch,
        formState: {errors, isValid}
    } = useForm<PhoneValidationSchema>({
        mode: 'onBlur',
        shouldFocusError: true,
        resolver: zodResolver(SmsOtpFlowSchema),
        defaultValues: {
            phone: phoneNumber ?? undefined
        }
    })

    const phoneNumberValue = watch('phone')
    const onSubmit: SubmitHandler<PhoneValidationSchema> = data => {
        setPhoneNumberValue(data.phone)
        requestVerificationPhoneCodeMutation.mutate({
            payload: {phone: data.phone}
        })
    }

    const isPending = requestVerificationPhoneCodeMutation.isPending
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody className="overflow-visible">
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('settings:personal_information:add_number_title')}</ModalTitle>
                        <ModalParagraph>{t('settings:personal_information:add_number_description')}</ModalParagraph>
                    </div>

                    <Controller
                        render={({field: {onChange, value}}) => (
                            <div className="flex w-full flex-col gap-1.5 [&_.react-tel-input>input]:relative [&_.react-tel-input>input]:flex [&_.react-tel-input>input]:h-11 [&_.react-tel-input>input]:w-full! [&_.react-tel-input_input]:cursor-pointer [&_.react-tel-input>input]:items-center [&_.react-tel-input>input]:justify-center [&_.react-tel-input>input]:gap-1 [&_.react-tel-input>input]:border [&_.react-tel-input>input]:border-neutral-300 [&_.react-tel-input>input]:py-0! [&_.react-tel-input>input]:pr-9! [&_.react-tel-input>input]:pl-12! [&_.react-tel-input>input]:text-md [&_.react-tel-input>input]:transition-all [&_.react-tel-input>input]:duration-200 [&_.react-tel-input>input]:ease-in-out [&_.react-tel-input>input:not(:disabled):hover]:border-primary-200 [&_.react-tel-input>input:not(:disabled):hover]:shadow-[0px_1px_2px_rgba(16,24,40,0.05),0px_0px_0px_4px_var(--color-primary-25)] [&_.react-tel-input>input:focus]:border-neutral-300 [&_.react-tel-input>input:focus]:shadow-none [&_.react-tel-input>input:focus]:outline-none [&_.react-tel-input>input::placeholder]:text-neutral-400 [&_.react-tel-input>input:disabled]:cursor-not-allowed [&_.react-tel-input_.country-list]:absolute [&_.react-tel-input_.country-list]:mx-auto [&_.react-tel-input_.country-list]:my-0 [&_.react-tel-input_.country-list]:max-h-[200px] [&_.react-tel-input_.country-list]:w-[90%] [&_.react-tel-input_.country-list]:overflow-x-hidden [&_.react-tel-input_.country-list]:rounded-lg [&_.react-tel-input_.country-list::-webkit-scrollbar]:w-1 [&_.react-tel-input_.country-list::-webkit-scrollbar]:bg-neutral-50 [&_.react-tel-input_.country-list::-webkit-scrollbar-thumb]:rounded-[3px] [&_.react-tel-input_.country-list::-webkit-scrollbar-thumb]:bg-neutral-300 [&_.react-tel-input_.country-list::-webkit-scrollbar-thumb:hover]:bg-neutral-400">
                                <Label htmlFor={'phone'}>{t('settings:personal_information:phone_number')}</Label>
                                <PhoneInput
                                    countryCodeEditable={true}
                                    enableAreaCodes={false}
                                    placeholder={t('settings:personal_information:phone_placeholder')}
                                    value={value}
                                    onChange={onChange}
                                />
                                <InputHelpText error={errors.phone?.message} />
                            </div>
                        )}
                        control={control}
                        name={'phone'}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="tertiary" onClick={onClose} disabled={isPending}>
                    {t('commons:cancel')}
                    {isPending && <Spinner />}
                </Button>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={isPending || !isValid || phoneNumberValue == phoneNumber}
                >
                    {t('commons:submit')}
                    {isPending && <Spinner />}
                </Button>
            </ModalFooter>
        </form>
    )
}
